import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/lib/api';
import { isProfileComplete } from '@/lib/profileCompletion';

interface User {
  id: string;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  birthday?: string | null;
  email?: string | null;
  role: string;
  isProfileComplete?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;
  isProfileComplete: boolean;
  
  // Actions
  login: (user: User, token: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setHydrated: (hydrated: boolean) => void;
  updateUser: (user: User) => void;
  checkAuth: () => Promise<boolean>;
  checkProfileCompletion: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isHydrated: false,
      isProfileComplete: false,

      login: (user: User, token: string) => {
        const frontendComplete = isProfileComplete(user);
        const backendComplete = user.isProfileComplete !== false;
        const profileComplete = frontendComplete && backendComplete;
        
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
          isProfileComplete: profileComplete,
        });
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          isProfileComplete: false,
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setHydrated: (hydrated: boolean) => {
        set({ isHydrated: hydrated });
      },

      updateUser: (user: User) => {
        const frontendComplete = isProfileComplete(user);
        const backendComplete = user.isProfileComplete !== false;
        const profileComplete = frontendComplete && backendComplete;
        
        set({ 
          user,
          isProfileComplete: profileComplete,
        });
      },

      checkAuth: async () => {
        const { token } = get();
        
        if (!token) {
          set({ isAuthenticated: false, user: null });
          return false;
        }

        try {
          const result = await api.getProfile() as any;
          
          if (!result.data) {
            throw new Error('Invalid user data');
          }

          const frontendComplete = isProfileComplete(result.data);
          const backendComplete = result.data.isProfileComplete !== false;
          const profileComplete = frontendComplete && backendComplete;
          
          set({
            user: result.data,
            isAuthenticated: true,
            isProfileComplete: profileComplete,
          });
          
          return true;
        } catch (error) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isProfileComplete: false,
          });
          return false;
        }
      },

      checkProfileCompletion: () => {
        const { user } = get();
        const profileComplete = isProfileComplete(user);
        set({ isProfileComplete: profileComplete });
        return profileComplete;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        isProfileComplete: state.isProfileComplete,
      }),
    }
  )
); 